// src/composables/useTimeLogout.js
import { onMounted, onUnmounted, watch } from "vue";
import { useAuthStore } from "@/stores/auth.js";
import { useBroadcastChannel } from "@vueuse/core";
import { debounce } from "lodash";

let lastActivity = Date.now();
let timer = null;
let warningTimer = null;
let stopTimerInternal = () => { };
let started = false; // evita reinicializaciones múltiples


export function stopTimer() {
  stopTimerInternal();
}

export function useIdleCounter(timeout) {
  const authStore = useAuthStore();
  timeout ??= (authStore.expires_in || 15) * 60 * 1000;

  const { post, data, error } = useBroadcastChannel({ name: "logout-channel" });

  const activityEvents = ["keydown", "mousedown", "touchstart"];
  // const activityEvents = ["mousemove", "keydown", "mousedown", "touchstart"];

  const safePost = (message) => {
    try {
      post(message);
    } catch (e) {
      console.warn("⚠️ Error al usar post(): canal posiblemente cerrado", e);
    }
  };

  const debouncedPostActivity = debounce(() => safePost("activity"), 500);
  let expired = false;
  let warningVisible = false;

  const closeForInactivity = () => {
    if (expired || !authStore.authUser || authStore.isLoggingOut) return;
    expired = true;
    // La sesión se invalida antes del aviso; el botón es únicamente informativo.
    authStore.logout();
    swal.fire({
      icon: "warning",
      title: "Sesión cerrada",
      text: "Cerramos tu sesión por inactividad.",
      confirmButtonText: "Entendido",
      allowOutsideClick: false,
    });
  };

  const resetTimer = ({ broadcast = true } = {}) => {
    if (expired || !authStore.authUser || authStore.isLoggingOut) return;
    if (Date.now() - lastActivity >= timeout) {
      closeForInactivity();
      return;
    }
    lastActivity = Date.now();
    clearTimeout(timer);
    clearTimeout(warningTimer);

    if (broadcast && document.visibilityState === "visible" && document.hasFocus()) {
      debouncedPostActivity(); // Solo si la pestaña está activa
      safePost("close-warning");
    }

    // Mostrar advertencia antes de cerrar sesión
    warningTimer = setTimeout(
      () => {
        warningVisible = true;
        swal
          .fire({
            icon: "info",
            title: "¿Sigues ahí?",
            text: "Tu sesión se cerrará en 1 minuto por inactividad.",
            confirmButtonText: "Continuar sesión",
            showConfirmButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            timer: 60 * 1000,
            timerProgressBar: true,
          })
          .then((result) => {
            warningVisible = false;
            if (result.isConfirmed) {
              resetTimer();
            }
          });
      },
      timeout - 60 * 1000
    );

    // Cierre automático por inactividad
    timer = setTimeout(() => {
      if (Date.now() - lastActivity >= timeout) {
        closeForInactivity();
      } else {
        // Usuario volvió del suspendido. Reiniciar timer.
        resetTimer();
      }
    }, timeout);
  };

  const startTimer = () => {
    if (started) return;
    started = true;
    lastActivity = Date.now();
    console.log("✅ Idle timer iniciado");
    activityEvents.forEach((event) =>
      window.addEventListener(event, resetTimer)
    );
    resetTimer();
  };

  const handleVisibilityChange = () => {
    // console.log(document.visibilityState);
    // console.log(document.hasFocus());
    if (document.visibilityState === "visible" && document.hasFocus()) {
      const now = Date.now();
      const elapsed = now - lastActivity;

      if (elapsed >= timeout) {
        closeForInactivity();
      } else {
        resetTimer(); // Todavía dentro del rango, reinicia
      }
    }
  };

  const handleFocus = () => {
    checkIdleAfterVisibility();
  };

  const checkIdleAfterVisibility = () => {
    const now = Date.now();
    const elapsed = now - lastActivity;

    if (elapsed >= timeout) {
      closeForInactivity();
    } else {
      resetTimer();
    }
  };

  stopTimerInternal = () => {
    activityEvents.forEach((event) =>
      window.removeEventListener(event, resetTimer)
    );
    clearTimeout(timer);
    clearTimeout(warningTimer);
    debouncedPostActivity.cancel();
    if (warningVisible) {
      warningVisible = false;
      swal.close();
    }
    started = false;
    console.trace("✅ Idle timer detenido");
  };

  // Escucha mensajes desde otras pestañas
  if (data) {
    watch(
      () => data.value,
      (value) => {
        if (value === "activity") {
          //   console.log(
          //     "🔄 Actividad detectada en otra pestaña, reiniciando temporizador"
          //   );
          resetTimer({ broadcast: false });
          data.value = null;
        }

        if (value === "close-warning" && warningVisible) {
          warningVisible = false;
          swal.close();
          data.value = null;
        }
      }
    );
  }

  // Maneja errores de canal
  if (error) {
    watch(
      () => error.value,
      (e) => {
        if (e) console.warn("🚨 Error en BroadcastChannel:", e);
      }
    );
  }

  onMounted(() => {
    if (authStore.authUser) {
      startTimer();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
  });

  onUnmounted(() => {
    stopTimer();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("focus", handleFocus);
  });
}
